/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  CRUDPermissions,
  GenericObject,
  Group
} from '@ion/core/src/types/entities';
import {DropDownDefaultLevelProps} from '@ion/dls/src/components/menus/Dropdown';
import {AdminGroupEditor} from '@ion/dls/src/editors/AdminGroupEditor';
import {viewDeviceById} from '../../devices/state';
import {viewAdminAsset} from '../assets/state';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import {editGroup, viewGroup} from './state';

export interface ViewGroupEditorProps extends DropDownDefaultLevelProps {
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  hasInheritableGroups?: boolean;
  groupsPermissions?: CRUDPermissions;
  ancestorsGroups?: GenericObject;
};

/**
 * Defines the ViewGroupEditor component, which contains the View action
 * to pass the selected Group as props to show the Group informations.
 */
export const ViewGroupEditor = ({
  groupsPermissions,
  ancestorsGroups,
  isChipClickable = false,
  includeDeviceGridCheckboxes = false,
  hasInheritableGroups = false,
  includeAssetGridCheckboxes = false
}: ViewGroupEditorProps) => {
  const dispatch                          = useDispatch();
  const {account}                         = useAuthenticatedUser();
  const {globalGroup}                     = useSelector(({globalGroup}) => globalGroup);
  const {selected: group, loading}        = useSelector(({groups: {view}}) => view);
  const [actualMembers, setActualMembers] = useState<Group>([]);

  const onEdit = useCallback(() => {
    dispatch(editGroup(group));
    dispatch(viewGroup(group, false));
  }, [group]);

  const getActualMembers = useCallback((groupMembers) => {
    setActualMembers(groupMembers);
  }, [actualMembers]);

  const onViewDevice = useCallback((deviceId) => {
    dispatch(viewDeviceById(deviceId) as any);
  }, []);

  const onViewAsset = useCallback((assetId) => {
    dispatch(viewAdminAsset(assetId, true, true) as any);
  }, []);

  return (
    <AdminGroupEditor open
      group={group}
      isChipClickable={isChipClickable}
      includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
      includeAssetGridCheckboxes={includeAssetGridCheckboxes}
      onViewDevice={onViewDevice}
      groupsPermissions={groupsPermissions}
      globalGroup={globalGroup}
      hasInheritableGroups={hasInheritableGroups}
      ancestorsGroups={ancestorsGroups}
      onViewAsset={onViewAsset}
      viewGroup
      viewChip
      account={globalGroup || account}
      getActualMembers={getActualMembers}
      onEdit={onEdit}
      loading={loading}
      onCloseDialog={function() {
        dispatch(viewGroup(null, false));
      }}/>
  );
};

export default ViewGroupEditor;
